<template>
  <div class="grow">
    <div class="flex items-center border bg-slate-50 p-1">
      <div class="grow flex items-center gap-1">
        <el-upload
          action="/api/v1/images"
          :data="{
            imagable_type: 'Document::Ai::Model',
            imagable_id: model.id,
          }"
          :show-file-list="false"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
        >
          <el-button size="mini" type="primary" plain>Upload</el-button>
        </el-upload>
        <el-popover placement="bottom" width="360" trigger="click">
          <ul class="trigger-marker flex items-center flex-wrap gap-0.5">
            <li
              v-for="(item, index) in model.images"
              :key="item.id"
              class="w-8 h-6 text-center"
              :class="{ active: image.uuid == item.uuid }"
              @click="load(item)"
            >
              <el-tooltip class="item" effect="dark" placement="bottom">
                <div slot="content">
                  <div>{{ item.filename }}</div>
                </div>
                <a class="no-underline">{{ index + 1 }}</a>
              </el-tooltip>
            </li>
          </ul>
          <el-button size="mini" type="primary" plain slot="reference"
            >Files ({{ model.images.length }})</el-button
          >
        </el-popover>
      </div>
      <div class="flex items-center gap-1">
        <el-popover placement="bottom" width="450" trigger="click">
          <div class="p-2">
            <fieldset class="form">
              <legend>File</legend>
              <slot v-if="model.file.error">
                <div class="tag w-full red my-1">Errors</div>
                <div class="h-60 overflow-auto">
                  <vue-json-pretty
                    :data="JSON.parse(model.file.error)"
                    :deep="3"
                    :show-line="true"
                    :show-double-quotes="false"
                  />
                </div>
              </slot>
              <div v-else class="flex flex-wrap gap-1">
                <div class="tag">ID: {{ model.file.id }}</div>
                <div class="tag">Status: {{ model.file.status }}</div>
                <div class="tag">Bytes: {{ model.file.bytes }}</div>
                <div class="tag">
                  Created:
                  {{ showTime(model.file.created_at) }}
                </div>
              </div>
            </fieldset>
            <fieldset class="form">
              <legend>Fine Tuning </legend>
              <div class="flex flex-wrap gap-1">
                <div class="tag">ID: {{ model.job.id }}</div>
                <div class="tag">Status: {{ model.job.status }}</div>
                <div class="tag">
                  Created:
                  {{ showTime(model.job.created_at) }}
                </div>
                <div class="tag">
                  Finished:
                  {{ showTime(model.job.finished_at) }}
                </div>
                <div class="tag">
                  Training File:
                  {{ model.job.training_file }}
                </div>
                <div class="tag">
                  Result Files:
                  {{ (model.job.result_files || []).join(", ") }}
                </div>
                <div class="tag"> Model: {{ model.job.model }} </div>
                <div class="tag">
                  FT Model: {{ model.job.fine_tuned_model }}
                </div>
                <div class="tag red" v-if="model.job.error">
                  Error: {{ model.job.error }}
                </div>
              </div>
            </fieldset>
            <fieldset class="form">
              <legend>Actions</legend>
              <div>
                <el-button
                  size="mini"
                  type="primary"
                  class="w-full"
                  :disabled="buildBtnDisabled"
                  @click="$emit('build', model)"
                >
                  Build Model (at least 10 examples)
                </el-button>
                <hr class="my-2" />
                <label class="text-xs">Give Job ID to Fetch Model</label>
                <div class="flex items-center gap-1">
                  <el-input
                    v-model="model.job_id"
                    size="mini"
                    placeholder="Model Name"
                    class="w-40"
                  />
                  <el-button
                    size="mini"
                    type="primary"
                    @click="$emit('save', model)"
                  >
                    Save
                  </el-button>
                </div>
              </div>
            </fieldset>
          </div>
          <el-button size="mini" type="primary" plain slot="reference"
            >FT</el-button
          >
        </el-popover>
        <el-button size="mini" type="primary" plain @click="dataset(model)"
          >Data</el-button
        >
        <el-button
          size="mini"
          type="primary"
          plain
          :disabled="!isEmptyData(model.images)"
          @click="$emit('remove', model)"
          >Delete
        </el-button>
      </div>
    </div>
    <div class="flex gap-1" v-if="image.uuid">
      <div class="w-96 e-form mt-1 overflow-auto h-[calc(100vh_-_160px)]">
        <h4>Model</h4>
        <el-select size="mini" v-model="image.imagable_id" class="w-full">
          <el-option
            v-for="model in models"
            :key="model.id"
            :label="model.name"
            :value="model.id"
          >
            <div class="flex items-center justify-between">
              <div>{{ model.name }}</div>
              <div>{{ fineTunedModel(model) }}</div>
            </div>
          </el-option>
        </el-select>
        <h4 class="flex items-center justify-between">
          <div>Expected JSON</div>
          <div class="flex items-center gap-1">
            <i v-if="onCompletion" class="el-icon-loading" />
            <el-dropdown size="mini" trigger="click" v-else>
              <a class="no-underline el-icon-more mx-1" />
              <el-dropdown-menu slot="dropdown" class="w-28">
                <el-dropdown-item
                  @click.native="completion(image, 'anthropic')"
                >
                  Anthropic
                </el-dropdown-item>
                <el-dropdown-item
                  @click.native="completion(image, 'open_ai_vision')"
                >
                  Open AI Vision
                </el-dropdown-item>
                <el-dropdown-item @click.native="completion(image, 'open_ai')">
                  Open AI OCR
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </h4>
        <textarea v-model="image.ocr.json" class="ocr-json" />
        <h4>Prompt</h4>
        <textarea v-model="image.ocr.prompt" class="w-full text-xs h-16" />
        <el-button-group class="my-2">
          <el-button size="mini" type="primary" @click="save">Save</el-button>
          <el-popconfirm
            title="Are you sure to delete this document?"
            confirmButtonText="Confirm"
            cancelButtonText="Cancel"
            @confirm="remove"
          >
            <el-button size="mini" type="danger" slot="reference"
              >Delete</el-button
            >
          </el-popconfirm>
        </el-button-group>
        <note-widget
          :id="image.id"
          type="Image"
          size="small"
          :key="image.uuid"
        />
      </div>
      <div class="grow e-form overflow-auto h-[calc(100vh_-_160px)]">
        <h4 class="flex items-center justify-between">
          <div class="flex items-center gap-1">
            <div>OCR Text</div>
          </div>
          <div class="flex items-center">
            <a class="no-underline text-xs" @click="openImage(image)"
              >Open Doc</a
            >
            <el-divider direction="vertical" />
            <slot v-if="image.ocr.trim_text">
              <a class="no-underline text-xs" @click="showText('raw')">Raw</a>
              <el-divider direction="vertical" />
              <a class="no-underline text-xs" @click="showText('trim')">Slim</a>
              <el-divider direction="vertical" />
            </slot>
            <e-copy :text="text" icon="none" show="Copy" className="text-xs" />
            <el-divider direction="vertical" />
            <span v-if="ocrPending" class="el-icon-loading" />
            <slot v-else>
              <el-tooltip
                placement="bottom"
                content="Detect Document Text"
                :open-delay="500"
              >
                <a class="no-underline text-xs" @click="extractText('detect')">
                  Detect Text
                </a>
              </el-tooltip>
            </slot>
          </div>
        </h4>
        <pre class="ocr-text"> {{ text }}</pre>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "DocumentAiModel",
  props: ["model", "models"],
  data() {
    return {
      image: {},
      text: "",
      onCompletion: false,
    };
  },
  computed: {
    isImage() {
      return this.image.content_type.includes("image");
    },
    isPdf() {
      return this.image.content_type.includes("pdf");
    },
    ocrPending() {
      return (this.image.ocr || {}).status == "pending";
    },
    isBuilding() {
      return this.model.status == "building";
    },
    buildBtnDisabled() {
      return this.isBuilding || this.model.images.length < 10;
    },
    showTime() {
      return function (time) {
        return time ? new Date(time * 1000).toLocaleString() : null;
      };
    },
    fineTunedModel() {
      return function (model) {
        const parts = (model.job.fine_tuned_model || "").split("::");
        return parts[parts.length - 1];
      };
    },
  },
  methods: {
    handleUploadSuccess(res, file) {
      this.model.images.push(res);
    },
    handleUploadError(err, file, fileList) {
      const message = JSON.parse(err.message);
      this.$message({ message: message.errors, type: "error", duration: 5000 });
    },
    load(item) {
      this.$http
        .get(`/api/v1/images/${item.uuid}?mode=ai&url=true`)
        .then((response) => {
          this.image = response.data;
          this.showText();
          if (this.ocrPending) this.loadText();
        })
        .catch((err) => {
          this.$message({ message: err.response.data.errors, type: "error" });
        });
    },
    save() {
      const { prompt, json } = this.image.ocr;
      this.$http
        .put(`/api/v1/images/${this.image.uuid}`, {
          imagable_id: this.image.imagable_id,
          ocr: { prompt, json },
        })
        .then((response) => {
          const image = this.model.images.find(
            (o) => o.uuid == this.image.uuid,
          );
          this.$set(image, "name", response.data.name);
          this.$message({ message: "Saved successfully", type: "success" });
        })
        .catch((err) => {
          this.$message({ message: err.response.data.errors, type: "error" });
        });
    },
    remove() {
      this.$http
        .delete(`/api/v1/images/${this.image.uuid}`)
        .then((response) => {
          let index = this.model.images.findIndex(
            (o) => o.uuid == this.image.uuid,
          );
          if (index >= 0) this.model.images.splice(index, 1);
          this.image = {};
        })
        .catch((err) => {
          this.$message({ message: err.response.data.errors, type: "error" });
        });
    },
    showText(mode) {
      if (mode == "raw") this.text = this.image.ocr.text;
      if (mode == "trim") this.text = this.image.ocr.trim_text;
      if (!mode) this.text = this.image.ocr.trim_text || this.image.ocr.text;
    },
    loadText() {
      this.$http
        .get(`/api/v1/textracts/0`, {
          params: {
            textual_id: this.image.id,
            textual_type: "Image",
            uploader: "file",
          },
        })
        .then((response) => {
          this.image.ocr = response.data;
          this.showText();
          if (this.ocrPending) setTimeout(() => this.loadText(), 10000);
        })
        .catch((err) => {});
    },
    extractText(mode) {
      this.$http
        .post("/api/v1/textracts", {
          textract: {
            textual_id: this.image.id,
            textual_type: "Image",
            uploader: "file",
          },
          mode,
        })
        .then((response) => {
          this.loadText();
        })
        .catch((err) => {});
    },
    dataset() {
      window.open(`/document/ai/models/${this.model.id}/dataset`);
    },
    async completion(image, cmd) {
      try {
        const { schema } = this.model;
        this.onCompletion = true;
        await this.$http.post(`/document/ai/images/${image.uuid}/completion`, {
          schema,
          cmd,
        });
        this.$message({
          message: `The process is running at the background`,
          type: "success",
        });
      } catch (err) {
        this.$message({ message: err.response.data.errors, type: "error" });
      } finally {
        this.onCompletion = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.ocr-text {
  @apply w-full h-[calc(100vh_-_205px)] py-2 text-[8px] overflow-auto bg-white whitespace-pre;
}
.ocr-json {
  @apply w-full h-[calc(100vh_-_600px)] mb-2 text-xs leading-4;
}
</style>
